import { useLocation } from "react-router-dom";
import { Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface PageInfo {
  title: string;
  description: string;
  features: string[];
}

const PAGE_SUMMARIES: Record<string, PageInfo> = {
  "/dashboard": {
    title: "Dashboard Principal",
    description: "Panel de control centralizado con visión general del negocio.",
    features: [
      "KPIs principales: ingresos, gastos, clientes activos y órdenes pendientes",
      "Gráfico de ingresos por periodo (día, semana, mes)",
      "Alertas de stock bajo y tareas pendientes",
      "Actividad reciente y citas próximas",
      "Acceso rápido a las acciones más utilizadas",
    ],
  },
  "/crm": {
    title: "CRM – Dashboard",
    description: "Resumen del estado comercial y relación con clientes.",
    features: [
      "Oportunidades activas y su valor total",
      "Pipeline de ventas por etapa",
      "Clientes nuevos vs. recurrentes",
      "Tareas y seguimientos pendientes",
    ],
  },
  "/crm/clients": {
    title: "CRM – Clientes",
    description: "Gestión completa de la cartera de clientes.",
    features: [
      "Listado de clientes con búsqueda y filtros",
      "Creación y edición de fichas de cliente",
      "Datos fiscales: NIF/CIF, dirección, email, teléfono",
      "Importación y exportación masiva desde Excel/CSV",
      "Historial de facturas y pedidos por cliente",
    ],
  },
  "/crm/contacts": {
    title: "CRM – Contactos",
    description: "Gestión de personas de contacto asociadas a clientes.",
    features: [
      "Listado de contactos con búsqueda",
      "Asociación de contactos a clientes",
      "Datos de contacto: email, teléfono, departamento, cargo",
    ],
  },
  "/crm/opportunities": {
    title: "CRM – Oportunidades",
    description: "Seguimiento de oportunidades de venta y pipeline comercial.",
    features: [
      "Creación y seguimiento de oportunidades",
      "Etapas del pipeline: prospecto, propuesta, negociación, cierre",
      "Valor estimado y probabilidad de cierre",
      "Asignación de responsables",
    ],
  },
  "/crm/tasks": {
    title: "CRM – Tareas",
    description: "Gestión de tareas y seguimientos comerciales.",
    features: [
      "Creación de tareas con fecha límite y prioridad",
      "Asignación a usuarios del equipo",
      "Estados: pendiente, en progreso, completada",
      "Notificaciones y recordatorios automáticos",
    ],
  },
  "/crm/appointments": {
    title: "CRM – Citas",
    description: "Calendario de citas con clientes.",
    features: [
      "Vista de calendario mensual, semanal y diaria",
      "Creación de citas con cliente, tipo, descripción",
      "Recordatorios automáticos por email",
      "Asociación con vehículos o productos",
    ],
  },
  "/crm/campaigns": {
    title: "CRM – Campañas",
    description: "Envío de campañas de email marketing.",
    features: [
      "Creación de campañas con asunto, contenido y destinatarios",
      "Segmentación de clientes por etiquetas o filtros",
      "Seguimiento de aperturas y clics",
      "Estadísticas de envío y tasa de conversión",
    ],
  },
  "/crm/zadarma": {
    title: "Zadarma – Dashboard",
    description: "Panel de telefonía IP con Zadarma.",
    features: [
      "Llamadas recientes entrantes y salientes",
      "Duración media de llamada",
      "Estadísticas por usuario",
    ],
  },
  "/crm/zadarma/history": {
    title: "Zadarma – Historial",
    description: "Historial completo de llamadas.",
    features: [
      "Listado de llamadas con filtros",
      "Grabaciones de llamadas (si activo)",
      "Asociación con clientes del CRM",
    ],
  },
  "/crm/zadarma/settings": {
    title: "Zadarma – Configuración",
    description: "Configuración de la integración con Zadarma.",
    features: [
      "API Key y API Secret de Zadarma",
      "Número de teléfono virtual",
      "Reglas de enrutamiento",
    ],
  },
  "/erp": {
    title: "ERP – Dashboard",
    description: "Visión general del inventario y operaciones.",
    features: [
      "Stock total y valor del inventario",
      "Productos más vendidos y con bajo stock",
      "Pedidos pendientes de proveedor",
      "Alertas de stock mínimo",
    ],
  },
  "/erp/products": {
    title: "ERP – Productos",
    description: "Catálogo completo de productos con gestión de stock.",
    features: [
      "Alta, edición y baja de productos",
      "Campos: nombre, SKU, código de barras, precio, coste, stock, IVA",
      "Categorización por marca, categoría y modelo",
      "Imágenes, variantes y descripciones multiidioma",
      "Importación masiva desde Excel",
      "Generación de etiquetas con código de barras",
    ],
  },
  "/erp/inventory": {
    title: "ERP – Inventario",
    description: "Control de existencias y movimientos de almacén.",
    features: [
      "Vista de stock actual por producto y almacén",
      "Movimientos de entrada y salida",
      "Ajustes de inventario y traspasos entre almacenes",
      "Importación masiva de inventario",
    ],
  },
  "/erp/categories": {
    title: "ERP – Categorías",
    description: "Organización jerárquica de productos.",
    features: [
      "Creación de categorías y subcategorías",
      "Asociación de productos a categorías",
      "Traducciones multiidioma",
    ],
  },
  "/erp/brands": {
    title: "ERP – Marcas",
    description: "Gestión de marcas de productos.",
    features: [
      "Alta y edición de marcas con logo",
      "Asociación a categorías",
      "Traducciones multiidioma",
    ],
  },
  "/erp/models": {
    title: "ERP – Modelos",
    description: "Gestión de modelos asociados a marcas.",
    features: [
      "Creación de modelos vinculados a marcas",
      "Filtrado y búsqueda de modelos",
    ],
  },
  "/erp/suppliers": {
    title: "ERP – Proveedores",
    description: "Gestión de proveedores y condiciones de compra.",
    features: [
      "Listado de proveedores con datos fiscales",
      "Condiciones de pago y plazos de entrega",
      "Historial de pedidos y facturas de compra",
    ],
  },
  "/erp/warehouses": {
    title: "ERP – Almacenes",
    description: "Configuración de ubicaciones de almacenamiento.",
    features: [
      "Alta de almacenes con dirección",
      "Asignación de stock por almacén",
      "Almacén principal y secundarios",
    ],
  },
  "/erp/purchase-invoices": {
    title: "ERP – Facturas de Compra",
    description: "Registro de facturas recibidas de proveedores.",
    features: [
      "Carga manual o automática de facturas de compra",
      "Asociación a pedidos de proveedor",
      "Desglose de IVA soportado deducible",
      "Procesamiento OCR de facturas escaneadas",
    ],
  },
  "/finance/internal-invoices": {
    title: "Finanzas – Facturas Internas",
    description: "Desglose contable de ventas con Recargo de Equivalencia.",
    features: [
      "Tarjetas con ventas sin/con servicio y total IVA",
      "Filtro por mes, trimestre o rango personalizado",
      "Exportación a PDF y Excel del período",
      "Paginación cada 25 registros",
    ],
  },
  "/erp/warranties": {
    title: "ERP – Garantías",
    description: "Gestión de garantías de productos.",
    features: [
      "Registro de garantías por producto y cliente",
      "Seguimiento de fechas de inicio y fin",
      "Estados de reclamación",
    ],
  },
  "/erp/labels": {
    title: "ERP – Etiquetas",
    description: "Generación e impresión de etiquetas de productos.",
    features: [
      "Generación de códigos de barras (EAN, Code128)",
      "Plantillas personalizables de etiquetas",
      "Impresión masiva por lote",
    ],
  },
  "/erp/bundles": {
    title: "ERP – Packs / Bundles",
    description: "Creación de packs de productos agrupados.",
    features: [
      "Composición de packs con múltiples productos",
      "Precio especial del pack",
      "Gestión de stock de componentes",
    ],
  },
  "/erp/service-costs": {
    title: "ERP – Coste de Servicio",
    description: "Gestión de servicios y sus costes para bundles.",
    features: [
      "Alta y edición de servicios",
      "Asignación de coste por servicio",
      "Vinculación de servicios a bundles",
    ],
  },
  "/erp/stock-report": {
    title: "ERP – Informe de Stock",
    description: "Reportes detallados del estado del inventario.",
    features: [
      "Stock actual por producto, categoría y almacén",
      "Valoración del inventario a precio de coste y venta",
      "Productos sin movimiento y obsoletos",
    ],
  },
  "/erp/import-ewheel": {
    title: "ERP – Importación Ewheel",
    description: "Scraping e importación de productos desde proveedor Ewheel.",
    features: [
      "Conexión automática con catálogo Ewheel",
      "Importación selectiva de productos",
      "Actualización de precios y stock",
    ],
  },
  "/erp/import-products": {
    title: "ERP – Importación de Productos",
    description: "Importación masiva de productos desde Excel.",
    features: [
      "Carga de archivo Excel/CSV",
      "Mapeo de columnas automático",
      "Validación y previsualización antes de importar",
      "Resumen de importación con errores",
    ],
  },
  "/finance": {
    title: "Finanzas – Dashboard",
    description: "Panel financiero con métricas de ingresos, gastos e impuestos.",
    features: [
      "Ingresos y gastos del periodo actual",
      "Gráficos de evolución mensual",
      "IVA repercutido vs soportado",
      "Beneficio neto y margen",
    ],
  },
  "/finance/invoices": {
    title: "Finanzas – Facturación Externas",
    description: "Emisión y gestión de facturas de venta.",
    features: [
      "Creación de facturas ordinarias, simplificadas y rectificativas",
      "Numeración automática por serie",
      "Líneas con producto, cantidad, precio, descuento e IVA",
      "Generación de PDF y envío por email",
      "Control de estado: borrador, emitida, cobrada, anulada",
      "Enlace con cobros y pagos",
    ],
  },
  "/finance/quotes": {
    title: "Finanzas – Presupuestos",
    description: "Creación y seguimiento de presupuestos comerciales.",
    features: [
      "Creación de presupuestos con líneas de producto",
      "Conversión automática a factura",
      "Estados: borrador, enviado, aceptado, rechazado",
      "Generación de PDF y envío por email",
    ],
  },
  "/finance/payments": {
    title: "Finanzas – Cobros y Pagos",
    description: "Control de cobros a clientes y pagos a proveedores.",
    features: [
      "Registro de cobros asociados a facturas",
      "Métodos de pago: efectivo, tarjeta, transferencia, bizum",
      "Conciliación con extractos bancarios",
      "Listado de pagos pendientes y vencidos",
    ],
  },
  "/finance/expenses": {
    title: "Finanzas – Gastos",
    description: "Registro y categorización de gastos del negocio.",
    features: [
      "Alta de gastos con categoría, proveedor e IVA",
      "Marcado de gastos deducibles / no deducibles",
      "Adjuntar justificantes y facturas de compra",
      "Gastos recurrentes automáticos",
    ],
  },
  "/finance/expense-categories": {
    title: "Finanzas – Categorías de Gastos",
    description: "Configuración de categorías para clasificar gastos.",
    features: [
      "Creación de categorías personalizadas",
      "Asignación de tipo fiscal (deducible IRPF, IVA soportado)",
    ],
  },
  "/finance/orders": {
    title: "Finanzas – Pedidos",
    description: "Gestión de pedidos de compra a proveedores.",
    features: [
      "Creación de pedidos con líneas de producto",
      "Estados: borrador, enviado, recibido, cancelado",
      "Recepción parcial o total de mercancía",
      "Generación automática de movimientos de stock",
    ],
  },
  "/finance/vouchers": {
    title: "Finanzas – Vales y Abonos",
    description: "Gestión de vales descuento y abonos a clientes.",
    features: [
      "Creación de vales con código, importe y fecha de caducidad",
      "Canje en punto de venta (TPV)",
      "Historial de uso por cliente",
    ],
  },
  "/finance/pos": {
    title: "Finanzas – Punto de Venta (TPV)",
    description: "Terminal punto de venta para ventas directas.",
    features: [
      "Interfaz rápida de cobro con búsqueda de productos",
      "Lectura de código de barras",
      "Métodos de pago múltiples",
      "Generación de ticket/factura simplificada",
      "Canje de vales descuento",
      "Cierre de caja diario",
    ],
  },
  "/finance/pos/history": {
    title: "Finanzas – Historial TPV",
    description: "Consulta de todas las ventas realizadas en el punto de venta.",
    features: [
      "Listado de ventas con filtros por fecha y método de pago",
      "Detalle de cada venta con productos y totales",
      "Exportación de datos",
    ],
  },
  "/finance/pos/analytics": {
    title: "Finanzas – Analíticas TPV",
    description: "Estadísticas avanzadas del punto de venta.",
    features: [
      "Ventas por hora, día y mes",
      "Productos más vendidos en TPV",
      "Ticket medio y número de transacciones",
      "Comparativa entre periodos",
    ],
  },
  "/finance/reconciliation": {
    title: "Finanzas – Conciliación Bancaria",
    description: "Importación y conciliación de extractos bancarios.",
    features: [
      "Importación de ficheros bancarios (Norma 43, CSV)",
      "Matching automático con facturas y gastos",
      "Conciliación manual de movimientos no reconocidos",
      "Reglas de conciliación automática personalizables",
    ],
  },
  "/finance/reconciliation-rules": {
    title: "Finanzas – Reglas de Conciliación",
    description: "Configuración de reglas para conciliación automática.",
    features: [
      "Creación de reglas por concepto, importe o proveedor",
      "Asignación automática de categoría y gasto",
      "Prioridad de reglas",
    ],
  },
  "/finance/autonomo": {
    title: "Finanzas – Dashboard Autónomo",
    description: "Panel fiscal adaptado al autónomo español (IVA general o recargo de equivalencia).",
    features: [
      "KPIs: saldo tesorería, IVA a liquidar, IRPF a cuenta (Mod. 130)",
      "Cálculo automático del Modelo 303 (IVA trimestral)",
      "Modelo 309 para adquisiciones especiales en recargo de equivalencia",
      "Modelo 130: pago fraccionado IRPF (20% del beneficio)",
      "Registro de ventas y gastos con desglose fiscal",
      "Calendario fiscal con alertas de vencimiento",
    ],
  },
  "/finance/empresa": {
    title: "Finanzas – Dashboard Empresa",
    description: "Panel fiscal para sociedades mercantiles en España.",
    features: [
      "KPIs: IVA neto, retenciones IRPF, provisión Impuesto Sociedades",
      "Modelo 303 (IVA), Modelo 111 (retenciones), Modelo 202 (IS a cuenta)",
      "Estado de cumplimiento SII/VeriFactu/TicketBAI",
      "Gráficos de ventas vs gastos y beneficio mensual",
      "Calendario fiscal con todas las obligaciones de sociedades",
      "Ratio de endeudamiento y punto de equilibrio",
    ],
  },
  "/settings/ftp": {
    title: "Finanzas – Configuración FTP",
    description: "Configuración de conexión FTP para envío de facturas.",
    features: [
      "Datos de conexión: servidor, puerto, usuario, contraseña",
      "Envío automático de facturas PDF a servidor FTP",
      "Test de conexión",
    ],
  },
  "/workshop": {
    title: "Taller – Dashboard",
    description: "Panel de control del taller de reparaciones.",
    features: [
      "Órdenes de trabajo activas y pendientes",
      "Ingresos del taller por periodo",
      "Tiempo medio de reparación",
      "Técnicos y carga de trabajo",
    ],
  },
  "/workshop/orders": {
    title: "Taller – Órdenes de Trabajo",
    description: "Gestión de reparaciones y servicios técnicos.",
    features: [
      "Creación de órdenes con cliente, vehículo y descripción",
      "Estados: recibida, en diagnóstico, en reparación, lista, entregada",
      "Asignación de técnico y tiempo estimado",
      "Líneas de piezas y mano de obra",
      "Generación de factura desde la orden",
      "Impresión de ticket de recepción",
    ],
  },
  "/workshop/rma": {
    title: "Taller – RMA / Garantías",
    description: "Gestión de devoluciones y reclamaciones de garantía.",
    features: [
      "Creación de tickets RMA con motivo y estado",
      "Seguimiento de piezas de repuesto",
      "Comunicación con cliente sobre el estado",
      "Resolución: reparación, cambio o abono",
    ],
  },
  "/workshop/vehicles": {
    title: "Taller – Vehículos",
    description: "Registro de vehículos asociados a clientes.",
    features: [
      "Alta de vehículos con marca, modelo, número de serie/chasis",
      "Historial de reparaciones por vehículo",
      "Asociación con cliente propietario",
    ],
  },
  "/workshop/history": {
    title: "Taller – Historial de Reparaciones",
    description: "Consulta del histórico completo de reparaciones.",
    features: [
      "Búsqueda por cliente, vehículo o periodo",
      "Detalle de cada reparación con piezas y costes",
      "Exportación de datos",
    ],
  },
  "/workshop/invoices": {
    title: "Taller – Facturas del Taller",
    description: "Facturas generadas desde órdenes de trabajo.",
    features: [
      "Listado de facturas del taller",
      "Generación automática desde orden de trabajo",
      "Envío por email y descarga PDF",
    ],
  },
  "/workshop/reports": {
    title: "Taller – Informes",
    description: "Reportes y estadísticas del taller.",
    features: [
      "Ingresos por periodo y tipo de reparación",
      "Rendimiento por técnico",
      "Productos más utilizados en reparaciones",
    ],
  },
  "/workshop/maintenance": {
    title: "Taller – Mantenimiento",
    description: "Gestión de mantenimientos programados.",
    features: [
      "Programación de mantenimientos preventivos",
      "Recordatorios automáticos por email",
      "Historial de mantenimientos realizados",
    ],
  },
  "/rental": {
    title: "Alquiler – Dashboard",
    description: "Panel de control de la flota de alquiler.",
    features: [
      "Vehículos disponibles vs. alquilados",
      "Ingresos por alquiler del periodo",
      "Reservas próximas y devoluciones pendientes",
    ],
  },
  "/rental/fleet": {
    title: "Alquiler – Flota",
    description: "Gestión de vehículos disponibles para alquiler.",
    features: [
      "Alta de vehículos con fotos, características y tarifas",
      "Estado: disponible, alquilado, en mantenimiento",
      "Localización GPS del vehículo",
    ],
  },
  "/rental/rentals": {
    title: "Alquiler – Alquileres",
    description: "Gestión de contratos de alquiler activos.",
    features: [
      "Creación de contratos con cliente, vehículo, fechas y tarifa",
      "Control de entregas y devoluciones",
      "Cargos adicionales por daños o retrasos",
      "Generación de factura al finalizar",
    ],
  },
  "/rental/categories": {
    title: "Alquiler – Categorías",
    description: "Clasificación de vehículos de alquiler por tipo.",
    features: [
      "Creación de categorías (scooter, bicicleta, patinete, etc.)",
      "Tarifas base por categoría",
    ],
  },
  "/rental/plans": {
    title: "Alquiler – Planes de Alquiler",
    description: "Configuración de planes y tarifas de alquiler.",
    features: [
      "Planes por hora, día, semana o mes",
      "Precios especiales y descuentos por duración",
    ],
  },
  "/rental/accessories": {
    title: "Alquiler – Accesorios",
    description: "Gestión de accesorios opcionales para alquileres.",
    features: [
      "Alta de accesorios con precio y stock",
      "Asociación a contratos de alquiler",
    ],
  },
  "/cms/blog": {
    title: "CMS – Dashboard del Blog",
    description: "Panel de control del blog y contenidos.",
    features: [
      "Posts publicados, borradores y programados",
      "Visitas totales y por post",
      "Comentarios pendientes de moderación",
      "Suscriptores del newsletter",
    ],
  },
  "/cms/blog/posts": {
    title: "CMS – Artículos",
    description: "Gestión de artículos del blog.",
    features: [
      "Creación y edición con editor visual (TipTap)",
      "Categorías, etiquetas e imagen destacada",
      "SEO: meta título, meta descripción, slug personalizado",
      "Programación de publicación y visibilidad",
      "Revisiones y versiones del contenido",
    ],
  },
  "/cms/blog/categories": {
    title: "CMS – Categorías y Etiquetas",
    description: "Organización del contenido del blog.",
    features: [
      "Creación de categorías con jerarquía",
      "Etiquetas para clasificación transversal",
      "Slugs personalizados para SEO",
    ],
  },
  "/cms/blog/comments": {
    title: "CMS – Comentarios",
    description: "Moderación de comentarios en artículos.",
    features: [
      "Listado de comentarios con estado (pendiente, aprobado, spam)",
      "Aprobación y eliminación masiva",
      "Respuestas a comentarios",
    ],
  },
  "/cms/blog/media": {
    title: "CMS – Medios",
    description: "Biblioteca de imágenes y archivos del blog.",
    features: [
      "Subida de imágenes con texto alternativo",
      "Galería con filtros por tipo y fecha",
      "Inserción directa en artículos",
    ],
  },
  "/cms/pages": {
    title: "CMS – Páginas",
    description: "Gestión de páginas estáticas del sitio web.",
    features: [
      "Creación de páginas legales, información, etc.",
      "Editor visual con bloques de contenido",
      "SEO y slug personalizado",
    ],
  },
  "/cms/landing": {
    title: "CMS – Editor de Landing",
    description: "Editor visual de la página de inicio pública.",
    features: [
      "Edición de secciones hero, servicios, testimonios",
      "Personalización de colores y textos",
      "Vista previa en tiempo real",
    ],
  },
  "/chatbot": {
    title: "Chatbot – Dashboard",
    description: "Panel de control del chatbot de WhatsApp.",
    features: [
      "Conversaciones activas y mensajes enviados",
      "Tasa de respuesta automática vs. manual",
      "Estadísticas de uso del bot",
    ],
  },
  "/chatbot/conversations": {
    title: "Chatbot – Conversaciones",
    description: "Gestión de conversaciones de WhatsApp Business.",
    features: [
      "Listado de conversaciones por cliente",
      "Respuesta manual desde la plataforma",
      "Etiquetado y asignación de conversaciones",
      "Historial de mensajes completo",
    ],
  },
  "/chatbot/knowledge": {
    title: "Chatbot – Base de Conocimiento",
    description: "Preguntas y respuestas automáticas del chatbot.",
    features: [
      "Creación de pares pregunta-respuesta",
      "Palabras clave para matching automático",
      "Categorización y prioridad",
      "Estadísticas de uso por respuesta",
    ],
  },
  "/chatbot/promotions": {
    title: "Chatbot – Promociones",
    description: "Envío de promociones por WhatsApp.",
    features: [
      "Creación de mensajes promocionales con imagen",
      "Segmentación de audiencia",
      "Programación de envío",
      "Estadísticas de entrega, lectura y clics",
    ],
  },
  "/chatbot/ads": {
    title: "Chatbot – Campañas de Anuncios",
    description: "Campañas publicitarias con landing en WhatsApp.",
    features: [
      "Creación de campañas con UTM tracking",
      "Mensaje de bienvenida personalizado por campaña",
      "Métricas: clics, conversaciones y conversiones",
    ],
  },
  "/chatbot/settings": {
    title: "Chatbot – Configuración",
    description: "Ajustes del chatbot de WhatsApp Business.",
    features: [
      "Token y Phone ID de WhatsApp Business API",
      "Mensaje de bienvenida y mensaje de fallback",
      "Horario de atención y zona horaria",
      "Activación/desactivación de IA",
    ],
  },
  "/gbp": {
    title: "Google Business – Dashboard",
    description: "Panel de control de fichas de Google Business Profile.",
    features: [
      "Perfiles conectados y su estado",
      "Reseñas recientes y valoración media",
      "Publicaciones activas",
    ],
  },
  "/gbp/profiles": {
    title: "Google Business – Perfiles",
    description: "Gestión de fichas de Google Business Profile.",
    features: [
      "Importación y conexión de perfiles de GBP",
      "Datos del negocio: nombre, dirección, teléfono, horarios",
      "Categorías y atributos",
    ],
  },
  "/gbp/posts": {
    title: "Google Business – Publicaciones",
    description: "Creación de publicaciones en Google Business.",
    features: [
      "Posts de tipo novedad, evento, oferta",
      "Programación de publicación",
      "Imagen, CTA y enlace de acción",
    ],
  },
  "/gbp/reviews": {
    title: "Google Business – Reseñas",
    description: "Gestión y respuesta a reseñas de clientes.",
    features: [
      "Listado de reseñas con valoración y fecha",
      "Respuesta directa a cada reseña",
      "Filtro por reseñas sin responder",
      "Media de valoración y estadísticas",
    ],
  },
  "/gbp/photos": {
    title: "Google Business – Fotos",
    description: "Gestión de fotos de la ficha de Google.",
    features: [
      "Subida de fotos por categoría (portada, interior, exterior, equipo)",
      "Descripción y etiquetado",
      "Eliminación de fotos obsoletas",
    ],
  },
  "/gbp/services": {
    title: "Google Business – Servicios",
    description: "Gestión de servicios mostrados en la ficha.",
    features: [
      "Alta de servicios con nombre, descripción y precio",
      "Activación/desactivación individual",
      "Edición y eliminación",
    ],
  },
  "/gbp/analytics": {
    title: "Google Business – Analíticas",
    description: "Estadísticas de rendimiento de las fichas.",
    features: [
      "Visitas al perfil y búsquedas",
      "Acciones: llamadas, rutas, visitas web",
      "Tendencias por periodo",
    ],
  },
  "/gbp/settings": {
    title: "Google Business – Configuración",
    description: "Configuración de la integración con Google Business.",
    features: [
      "Conexión OAuth con Google",
      "Selección de cuentas y fichas",
      "Frecuencia de sincronización",
    ],
  },
  "/admin/gbp": {
    title: "Google Business – Administración",
    description: "Panel de administración avanzada de GBP.",
    features: [
      "Gestión de accesos y permisos",
      "Logs de actividad y sincronización",
    ],
  },
  "/hrm": {
    title: "RRHH – Dashboard",
    description: "Panel de recursos humanos.",
    features: [
      "Empleados activos, nuevas incorporaciones y bajas",
      "Ausencias y vacaciones pendientes",
      "Nóminas del mes",
    ],
  },
  "/hrm/employees": {
    title: "RRHH – Empleados",
    description: "Gestión del personal de la empresa.",
    features: [
      "Ficha de empleado: datos personales, contrato, salario",
      "Departamento, posición y responsable directo",
      "Documentación adjunta",
    ],
  },
  "/hrm/departments": {
    title: "RRHH – Departamentos",
    description: "Organización departamental de la empresa.",
    features: [
      "Creación de departamentos",
      "Asignación de responsables",
      "Número de empleados por departamento",
    ],
  },
  "/hrm/positions": {
    title: "RRHH – Posiciones",
    description: "Catálogo de puestos de trabajo.",
    features: [
      "Definición de posiciones con descripción y requisitos",
      "Rango salarial asociado",
    ],
  },
  "/hrm/payroll": {
    title: "RRHH – Nóminas",
    description: "Gestión de nóminas y retribuciones.",
    features: [
      "Generación de nóminas mensuales",
      "Cálculo de IRPF, Seguridad Social y neto",
      "Historial de pagos por empleado",
    ],
  },
  "/hrm/time-off": {
    title: "RRHH – Ausencias y Vacaciones",
    description: "Control de ausencias, permisos y vacaciones.",
    features: [
      "Solicitud y aprobación de vacaciones",
      "Tipos: vacaciones, baja médica, asuntos propios",
      "Calendario de ausencias del equipo",
    ],
  },
  "/hrm/recruitment": {
    title: "RRHH – Selección",
    description: "Gestión de procesos de selección y candidatos.",
    features: [
      "Publicación de ofertas de empleo",
      "Seguimiento de candidatos por etapas",
      "Entrevistas y valoraciones",
    ],
  },
  "/hrm/training": {
    title: "RRHH – Formación",
    description: "Gestión de formación y capacitación.",
    features: [
      "Planes de formación por empleado o departamento",
      "Registro de cursos completados",
      "Certificaciones y caducidades",
    ],
  },
  "/settings/general": {
    title: "Configuración – General",
    description: "Ajustes generales del negocio.",
    features: [
      "Datos de la empresa: nombre, NIF, dirección, teléfono",
      "Logo de la empresa",
      "Tipo de negocio: autónomo o empresa",
      "Régimen de IVA: general o recargo de equivalencia",
      "Moneda y formato de números",
    ],
  },
  "/settings/security": {
    title: "Configuración – Seguridad",
    description: "Ajustes de seguridad y autenticación.",
    features: [
      "Autenticación en dos factores (2FA/TOTP)",
      "Protección contra captura de pantalla",
      "Gestión de sesiones activas",
    ],
  },
  "/settings/notifications": {
    title: "Configuración – Notificaciones",
    description: "Preferencias de notificaciones del sistema.",
    features: [
      "Activar/desactivar notificaciones por módulo",
      "Canales: email, push, in-app",
      "Frecuencia de resúmenes",
    ],
  },
  "/settings/communications": {
    title: "Configuración – Comunicaciones",
    description: "Configuración de canales de comunicación.",
    features: [
      "Integración con email (SMTP/Resend)",
      "Integración con WhatsApp (Twilio)",
      "Plantillas de email personalizables",
    ],
  },
  "/settings/smtp": {
    title: "Configuración – SMTP",
    description: "Configuración del servidor de correo SMTP.",
    features: [
      "Datos de conexión: host, puerto, usuario, contraseña",
      "Remitente predeterminado",
      "Test de envío",
    ],
  },
  "/settings/resend": {
    title: "Configuración – Resend",
    description: "Integración con el servicio de email Resend.",
    features: [
      "API Key de Resend",
      "Dominio verificado",
      "Test de envío",
    ],
  },
  "/settings/twilio": {
    title: "Configuración – Twilio",
    description: "Integración con Twilio para SMS y WhatsApp.",
    features: [
      "Account SID, Auth Token y número de teléfono",
      "Test de envío de mensaje",
    ],
  },
  "/settings/email-templates": {
    title: "Configuración – Plantillas de Email",
    description: "Personalización de plantillas de correo electrónico.",
    features: [
      "Plantillas para facturas, presupuestos, recordatorios",
      "Variables dinámicas (nombre, importe, fecha)",
      "Vista previa y test de envío",
    ],
  },
  "/settings/i18n": {
    title: "Configuración – Idiomas",
    description: "Configuración de idiomas del sistema.",
    features: [
      "Idioma principal y idiomas secundarios",
      "Traducciones de productos, categorías y marcas",
    ],
  },
  "/settings/subscription": {
    title: "Configuración – Suscripción",
    description: "Gestión del plan de suscripción.",
    features: [
      "Plan actual y fecha de renovación",
      "Historial de pagos",
      "Cambio de plan y método de pago",
    ],
  },
  "/users": {
    title: "Usuarios",
    description: "Gestión de usuarios del sistema.",
    features: [
      "Listado de usuarios con rol y estado",
      "Invitación de nuevos usuarios por email",
      "Asignación de roles y permisos",
      "Desactivación de cuentas",
    ],
  },
  "/users/roles": {
    title: "Roles y Permisos",
    description: "Configuración de roles de acceso al sistema.",
    features: [
      "Creación de roles personalizados",
      "Asignación de permisos por módulo (ver, crear, editar, eliminar)",
      "Rol de administrador con acceso completo",
    ],
  },
  "/notifications": {
    title: "Notificaciones",
    description: "Centro de notificaciones del sistema.",
    features: [
      "Listado de notificaciones recibidas",
      "Marcar como leída/no leída",
      "Filtro por tipo y fecha",
    ],
  },
  "/notifications/preferences": {
    title: "Preferencias de Notificaciones",
    description: "Configuración personal de notificaciones.",
    features: [
      "Selección de tipos de notificación a recibir",
      "Canales preferidos: email, push, in-app",
      "Horarios de no molestar",
    ],
  },
  "/reports": {
    title: "Informes",
    description: "Generación de informes del negocio.",
    features: [
      "Informes de ventas, gastos y beneficio por periodo",
      "Informes de IVA trimestral (Mod. 303)",
      "Informes de stock y valoración de inventario",
      "Exportación a Excel y PDF",
    ],
  },
  "/documents": {
    title: "Documentos",
    description: "Gestión documental centralizada.",
    features: [
      "Almacenamiento de documentos con metadatos",
      "Compartir documentos con enlace público",
      "Organización por carpetas y etiquetas",
    ],
  },
  "/integrations/shipping": {
    title: "Integraciones – Envíos",
    description: "Gestión de envíos con transportistas.",
    features: [
      "Creación de envíos con tracking",
      "Integración con Envía, MRW, SEUR, etc.",
      "Etiquetas de envío descargables",
    ],
  },
  "/integrations/shipping/settings": {
    title: "Integraciones – Config. Envíos",
    description: "Configuración de transportistas y tarifas.",
    features: [
      "API keys de cada transportista",
      "Tarifas y zonas de envío",
      "Transportista predeterminado",
    ],
  },
  "/integrations/woocommerce/orders": {
    title: "Integraciones – Pedidos WooCommerce",
    description: "Sincronización de pedidos desde WooCommerce.",
    features: [
      "Importación automática de pedidos",
      "Sincronización de estados",
      "Asociación con productos locales",
    ],
  },
  "/integrations/woocommerce": {
    title: "Integraciones – Config. WooCommerce",
    description: "Configuración de la conexión con WooCommerce.",
    features: [
      "URL de la tienda, Consumer Key y Secret",
      "Frecuencia de sincronización",
      "Mapeo de estados de pedido",
    ],
  },
  "/integrations/marketplaces": {
    title: "Integraciones – Marketplaces",
    description: "Dashboard de ventas en marketplaces.",
    features: [
      "Resumen de ventas por marketplace",
      "Stock sincronizado entre canales",
    ],
  },
  "/integrations/marketplaces/products": {
    title: "Integraciones – Productos en Marketplaces",
    description: "Gestión de productos publicados en marketplaces.",
    features: [
      "Listado de productos sincronizados",
      "Publicación y despublicación",
      "Precios y stock por canal",
    ],
  },
  "/integrations/marketplaces/settings": {
    title: "Integraciones – Config. Marketplaces",
    description: "Configuración de conexiones con marketplaces.",
    features: [
      "Credenciales de Amazon, eBay, etc.",
      "Reglas de precios por canal",
    ],
  },
  "/insurance": {
    title: "Seguros – Dashboard",
    description: "Panel de gestión de seguros.",
    features: [
      "Pólizas activas y próximas a vencer",
      "Primas cobradas y pendientes",
      "Siniestros abiertos",
    ],
  },
  "/insurance/clients": {
    title: "Seguros – Clientes",
    description: "Gestión de asegurados.",
    features: [
      "Ficha de asegurado con datos personales y pólizas",
      "Historial de siniestros",
      "Documentación asociada",
    ],
  },
  "/insurance/clients/new": {
    title: "Seguros – Nuevo Cliente",
    description: "Formulario de alta de nuevo asegurado.",
    features: [
      "Datos personales y de contacto",
      "Selección de tipo de seguro",
      "Documentación requerida",
    ],
  },
  "/insurance/types": {
    title: "Seguros – Tipos de Seguro",
    description: "Catálogo de productos de seguro.",
    features: [
      "Tipos: responsabilidad civil, robo, accidentes, etc.",
      "Coberturas y exclusiones",
      "Tarifas base",
    ],
  },
  "/insurance/settings": {
    title: "Seguros – Configuración",
    description: "Ajustes del módulo de seguros.",
    features: [
      "Datos de la correduría",
      "Integración con aseguradoras",
      "Plantillas de pólizas",
    ],
  },
  "/registrations": {
    title: "Matriculaciones – Dashboard",
    description: "Panel de gestión de matriculaciones de vehículos.",
    features: [
      "Matriculaciones en curso y completadas",
      "Documentación pendiente",
      "Estadísticas por periodo",
    ],
  },
  "/registrations/list": {
    title: "Matriculaciones – Listado",
    description: "Listado de todas las matriculaciones.",
    features: [
      "Filtros por estado, fecha y tipo de vehículo",
      "Detalle de cada expediente",
      "Documentación adjunta",
    ],
  },
  "/registrations/settings": {
    title: "Matriculaciones – Configuración",
    description: "Ajustes del módulo de matriculaciones.",
    features: [
      "Tipos de vehículo y requisitos documentales",
      "Tasas y aranceles",
    ],
  },
  "/formation": {
    title: "Formación – Dashboard",
    description: "Panel de gestión de cursos y formaciones.",
    features: [
      "Cursos activos y próximos",
      "Alumnos inscritos",
      "Certificados emitidos",
    ],
  },
  "/admin/formation": {
    title: "Formación – Administración",
    description: "Gestión administrativa de formaciones.",
    features: [
      "Creación de cursos con temario y horarios",
      "Gestión de inscripciones y alumnos",
      "Emisión de certificados",
      "Configuración de plantillas de formación",
    ],
  },
  "/help": {
    title: "Ayuda – Centro de Soporte",
    description: "Centro de ayuda y documentación.",
    features: [
      "Guías de uso por módulo",
      "Preguntas frecuentes",
      "Contacto con soporte técnico",
    ],
  },
  "/admin/help": {
    title: "Ayuda – Administración",
    description: "Gestión del contenido de ayuda.",
    features: [
      "Creación y edición de artículos de ayuda",
      "Organización por categorías",
    ],
  },
  "/manufacturing/designer": {
    title: "Fabricación – Diseñador de Baterías",
    description: "Diseñador visual de packs de baterías.",
    features: [
      "Configuración de serie (S) y paralelo (P)",
      "Visualización 3D del pack",
      "Cálculo de voltaje, capacidad, peso y coste",
      "Exportación a PDF",
    ],
  },
  "/manufacturing/designer-history": {
    title: "Fabricación – Historial de Diseños",
    description: "Historial de diseños de baterías guardados.",
    features: [
      "Listado de diseños con configuración y fecha",
      "Carga de diseños anteriores",
      "Comparación entre diseños",
    ],
  },
  "/manufacturing/cell-specs": {
    title: "Fabricación – Especificaciones de Celdas",
    description: "Catálogo de celdas de batería disponibles.",
    features: [
      "Datos técnicos: voltaje, capacidad, descarga máxima, peso",
      "Formatos: 18650, 21700, pouch, prismática",
      "Costes y proveedores",
      "Stock de celdas",
    ],
  },
  "/manufacturing/costes": {
    title: "Fabricación – Costes de Producción",
    description: "Cálculo de costes de fabricación de baterías.",
    features: [
      "Coste de materiales por pack",
      "Mano de obra y overhead",
      "Margen de beneficio",
    ],
  },
  "/manufacturing/balanceo": {
    title: "Fabricación – Balanceo de Celdas",
    description: "Herramienta de balanceo y emparejamiento de celdas.",
    features: [
      "Agrupación de celdas por voltaje y resistencia interna",
      "Algoritmo de emparejamiento óptimo",
      "Informe de desviación",
    ],
  },
  "/local-web/analytics": {
    title: "Web Local – Analíticas",
    description: "Estadísticas de webs locales generadas.",
    features: [
      "Visitas y conversiones por web local",
      "Formularios de contacto recibidos",
      "Rendimiento SEO",
    ],
  },
  "/ecommerce/orders": {
    title: "E-commerce – Pedidos",
    description: "Gestión de pedidos de la tienda online.",
    features: [
      "Listado de pedidos con estado y totales",
      "Detalle de productos, envío y pago",
      "Cambio de estado y notificación al cliente",
    ],
  },
  "/ecommerce/coupons": {
    title: "E-commerce – Cupones",
    description: "Gestión de cupones de descuento.",
    features: [
      "Creación de cupones con código, tipo y valor",
      "Fecha de validez y límite de usos",
      "Estadísticas de uso",
    ],
  },
  "/admin/saas": {
    title: "Admin – Gestión SaaS",
    description: "Panel de administración de la plataforma SaaS.",
    features: [
      "Gestión de planes y suscripciones",
      "Suscriptores activos y MRR",
      "Métodos de pago y verificaciones",
    ],
  },
  "/admin/local-websites": {
    title: "Admin – Webs Locales",
    description: "Gestión de micrositios web locales.",
    features: [
      "Creación de webs para negocios locales",
      "Personalización de diseño, textos e imágenes",
      "Horarios de apertura y formulario de contacto",
      "SEO local y Google Maps",
    ],
  },
  "/admin/data-duplication": {
    title: "Admin – Duplicación de Datos",
    description: "Herramienta para duplicar datos entre workspaces.",
    features: [
      "Selección de tablas y datos a duplicar",
      "Duplicación de productos, categorías, marcas, etc.",
      "Prevención de duplicados",
    ],
  },
};

export function PageSummary() {
  const location = useLocation();
  const path = location.pathname;

  // Find exact match or closest parent match
  const info = PAGE_SUMMARIES[path];

  if (!info) return null;

  return (
    <div className="mt-12 mb-4">
      <Separator className="mb-6" />
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="space-y-2">
              <div>
                <h3 className="font-semibold text-sm">{info.title}</h3>
                <p className="text-sm text-muted-foreground">{info.description}</p>
              </div>
              <ul className="space-y-1">
                {info.features.map((f, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
